import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { IntlProvider } from 'react-intl'
import { logWarning } from '@/services/errorService'

import { PageLoader } from '@/components/common/PageLoader'
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from '@/constants/localeConstants'
import enUSMessages from '../translations/en-US.json'
import ltLTMessages from '../translations/lt-LT.json'

// All currently supported locales are pre-seeded at module init. This avoids hanging
// dynamic imports in test environments and ensures instant locale switches in production
// (the JSON is small — the cost of bundling both is lower than the latency of a lazy fetch).
const messagesCache = new Map<string, Record<string, string>>([
	['en-US', enUSMessages],
	['lt-LT', ltLTMessages],
])

function loadMessages(locale: string): Promise<Record<string, string>> {
	return Promise.resolve(
		messagesCache.get(locale) ?? messagesCache.get(DEFAULT_LOCALE) ?? enUSMessages,
	)
}

type DetectLocaleResult = { locale: string; isUserOverridden: boolean }

function detectLocale(): DetectLocaleResult {
	const searchParams = new URLSearchParams(window.location.search)
	const queryLang = searchParams.get('lang')
	if (queryLang && SUPPORTED_LOCALES.includes(queryLang as (typeof SUPPORTED_LOCALES)[number])) {
		return { locale: queryLang, isUserOverridden: true }
	}

	const browserLang = navigator.language
	if (SUPPORTED_LOCALES.includes(browserLang as (typeof SUPPORTED_LOCALES)[number])) {
		return { locale: browserLang, isUserOverridden: false }
	}

	const langPrefix = browserLang.split('-')[0]
	const match = SUPPORTED_LOCALES.find((l) => l.startsWith(langPrefix))
	return { locale: match ?? DEFAULT_LOCALE, isUserOverridden: false }
}

interface I18nContextValue {
	locale: string
	/**
	 * STO-3809: True when the user has explicitly chosen a locale via `?lang=` query param.
	 * When true, merchant-driven locale (countryCode → locale) must not override the user's choice.
	 */
	isUserOverridden: boolean
	changeLocale: (locale: string) => void
}

const I18nContext = createContext<I18nContextValue>({
	locale: DEFAULT_LOCALE,
	isUserOverridden: false,
	changeLocale: () => undefined,
})

export function useLocale(): I18nContextValue {
	return useContext(I18nContext)
}

interface I18nProviderProps {
	children: React.ReactNode
}

export function I18nProvider({ children }: I18nProviderProps) {
	const [{ locale, isUserOverridden }, setLocaleState] = useState<DetectLocaleResult>(() =>
		detectLocale(),
	)
	const [messages, setMessages] = useState<Record<string, string> | null>(null)

	useEffect(() => {
		let cancelled = false
		void loadMessages(locale)
			.then((msgs) => {
				if (!cancelled) setMessages(msgs)
			})
			.catch((error: unknown) => {
				logWarning(
					'[I18nProvider] Unexpected error loading messages, falling back to en-US',
					{
						locale,
						error,
					},
				)
				if (!cancelled) setMessages(enUSMessages)
			})
		return () => {
			cancelled = true
		}
	}, [locale])

	const changeLocale = useCallback((newLocale: string) => {
		if (SUPPORTED_LOCALES.includes(newLocale as (typeof SUPPORTED_LOCALES)[number])) {
			setLocaleState((prev) => {
				if (prev.locale === newLocale) return prev // already the requested locale — skip re-render
				return { ...prev, locale: newLocale }
			})
		}
	}, [])

	const contextValue = useMemo(
		() => ({ locale, isUserOverridden, changeLocale }),
		[locale, isUserOverridden, changeLocale],
	)

	if (!messages) {
		return <PageLoader />
	}

	return (
		<I18nContext.Provider value={contextValue}>
			<IntlProvider locale={locale} messages={messages} defaultLocale={DEFAULT_LOCALE}>
				{children}
			</IntlProvider>
		</I18nContext.Provider>
	)
}
